/**
 * How a block edits itself.
 *
 * React Flow hands a node only its own data, so the write path reaches the
 * blocks through context rather than through node data — putting callbacks in
 * node data would rebuild every node on each render.
 */

import React, { useContext } from 'react';
import { AOPDraftStep } from '../../types/AOPTypes';

export interface AOPEditorApi {
	/** Merge a partial into one step of the plan. */
	updateStep: (stepId: string, patch: Partial<AOPDraftStep>) => void;
	/**
	 * Swaps a step for a different one, keeping its name so everything pointing
	 * at it still lands. Unlike `updateStep` this does not merge: a replacement
	 * must not inherit fields of the step it replaced, and every step shape
	 * rejects a field it does not declare.
	 */
	replaceStep: (stepId: string, next: AOPDraftStep) => void;
	removeStep: (stepId: string) => void;
	/**
	 * True when the canvas is showing a published version rather than the draft.
	 * Versions are immutable, so a block renders its summary and nothing else —
	 * no inline editors, no delete. Editing one means copying it into the draft.
	 */
	isReadOnly: boolean;
}

const noop = () => undefined;

export const AOPEditorContext = React.createContext<AOPEditorApi>({
	updateStep: noop,
	replaceStep: noop,
	removeStep: noop,
	isReadOnly: false,
});

export const useAOPEditor = (): AOPEditorApi => useContext(AOPEditorContext);
